import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Building2, Users, Newspaper, Ticket, Activity, FileCheck, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

const PRIORITY_COLORS: Record<string, string> = {
  urgent:   'bg-red-100 text-red-700',
  high:     'bg-orange-100 text-orange-700',
  medium:   'bg-amber-100 text-amber-700',
  low:      'bg-muted text-muted-foreground',
}

const ONBOARDING_STEPS: { key: string; label: string; action?: string; actionLabel?: string }[] = [
  { key: 'cco_assigned',      label: 'Institution Admin appointed',   action: 'organisations', actionLabel: 'Appoint now' },
  { key: 'brand_set',         label: 'Brand colours configured',      action: 'organisations', actionLabel: 'Configure' },
  { key: 'charter_uploaded',  label: 'Institution charter uploaded',  action: 'organisations', actionLabel: 'Upload charter' },
  { key: 'first_member',      label: 'First team member added',       action: 'users',         actionLabel: 'Add member' },
  { key: 'first_journalist',  label: 'First journalist registered' },
  { key: 'first_event',       label: 'First event created' },
  { key: 'media_kit_uploaded', label: 'Media kit uploaded' },
]

interface Props {
  onNavigate: (view: string) => void
}

export function OverviewView({ onNavigate }: Props) {
  const qc = useQueryClient()

  const { data: metrics } = useQuery({
    queryKey: ['platform-metrics'],
    queryFn: async () => {
      const { data } = await supabase
        .from('platform_metrics')
        .select('*')
        .single()
      return data
    }
  })

  const { data: recentOrgs } = useQuery({
    queryKey: ['recent-orgs'],
    queryFn: async () => {
      const { data } = await (supabase
        .from('organizations')
        .select('id, name, created_at, settings')
        .not('settings->>is_platform_owner', 'eq', 'true')
        .order('created_at', { ascending: false })
        .limit(5) as any)
      return (data ?? []) as any[]
    }
  })

  const { data: openTickets } = useQuery({
    queryKey: ['open-tickets-preview'],
    queryFn: async () => {
      const { data } = await (supabase
        .from('support_tickets' as any)
        .select('id, subject, priority, created_at')
        .eq('status', 'open')
        .order('created_at', { ascending: false })
        .limit(5) as any)
      return (data ?? []) as any[]
    }
  })

  const { data: platformActivity } = useQuery({
    queryKey: ['platform-activity-all'],
    queryFn: async () => {
      const { data } = await supabase
        .from('activity_log')
        .select('id, actor_name, action_type, description, created_at, organizations(name)')
        .order('created_at', { ascending: false })
        .limit(15)
      return data ?? []
    }
  })

  // Institutions with no active CCO assigned
  const { data: orgsWithoutCCO } = useQuery({
    queryKey: ['orgs-without-cco'],
    queryFn: async () => {
      const { data } = await (supabase
        .from('organizations')
        .select(`
          id, name,
          organization_members(id, roles(name), status)
        `)
        .not('settings->>is_platform_owner', 'eq', 'true') as any)

      return ((data || []) as any[]).filter((org: any) =>
        !org.organization_members?.some(
          (m: any) => m.roles?.name === 'CCO' && m.status === 'active'
        )
      )
    }
  })

  // Onboarding progress per institution
  const { data: onboardingByOrg } = useQuery({
    queryKey: ['admin-onboarding-progress'],
    queryFn: async () => {
      const { data } = await (supabase
        .from('organizations')
        .select('id, name, org_onboarding(*)')
        .not('settings->>is_platform_owner', 'eq', 'true')
        .order('name') as any)
      return (data || []) as any[]
    }
  })

  useEffect(() => {
    const channel = supabase
      .channel('platform-activity-all')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'activity_log' }, () => {
        qc.invalidateQueries({ queryKey: ['platform-activity-all'] })
      })
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [qc])

  const metricCards = [
    { label: 'Total Institutions', value: metrics?.tenant_count ?? '', icon: Building2, color: 'text-blue-600' },
    { label: 'Total Org Users', value: metrics?.total_org_users ?? '', icon: Users, color: 'text-emerald-600' },
    { label: 'Active Journalists', value: metrics?.total_journalists ?? '', icon: Newspaper, color: 'text-violet-600' },
    { label: 'Active Accreditations', value: metrics?.active_accreditations ?? '', icon: FileCheck, color: 'text-teal-600' },
    { label: 'Open Tickets', value: metrics?.open_tickets ?? '', icon: Ticket, color: 'text-amber-600', highlight: (metrics?.open_tickets ?? 0) > 0 },
  ]

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Platform Overview</h1>
        <p className="text-justify text-muted-foreground text-sm mt-1">Real-time snapshot of the CONORBIT platform.</p>
      </div>

      {/* No-CCO alert */}
      {(orgsWithoutCCO?.length ?? 0) > 0 && (
        <div className="bg-amber-50 border border-amber-200 border-l-4 border-l-amber-500 rounded-lg p-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <AlertCircle size={16} className="text-amber-600 flex-shrink-0" />
            <span className="text-[13px] text-amber-700 text-justify">
              <strong>{orgsWithoutCCO!.length}</strong> institution{orgsWithoutCCO!.length > 1 ? 's have' : ' has'} no
              Institution Admin assigned. {orgsWithoutCCO!.map((o: any) => o.name).join(', ')}
            </span>
          </div>
          <button
            onClick={() => onNavigate('organisations')}
            className="text-[12px] text-amber-700 font-medium hover:underline flex-shrink-0 ml-3"
          >
            Appoint now →
          </button>
        </div>
      )}

      {/* Metric cards */}
      <div className="grid grid-cols-2 xl:grid-cols-5 gap-4">
        {metricCards.map(card => {
          const Icon = card.icon
          return (
            <Card key={card.label} className={cn(card.highlight && 'bg-amber-50 border-amber-200')}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {card.label}
                  </span>
                  <Icon className={cn('w-4 h-4', card.color)} />
                </div>
                <div className={cn('text-3xl font-bold', card.highlight ? 'text-amber-700' : 'text-foreground')}>
                  {card.value}
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Recent institutions */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Recent Institutions</CardTitle>
          </CardHeader>
          <CardContent>
            {(recentOrgs ?? []).length === 0 ? (
              <p className="text-justify text-sm text-muted-foreground">No institutions yet.</p>
            ) : (
              <ul className="space-y-3">
                {(recentOrgs ?? []).map((org: any) => (
                  <li key={org.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded bg-blue-100 flex items-center justify-center text-[10px] font-bold text-blue-700">
                        {org.name?.[0] ?? '?'}
                      </div>
                      <span className="text-sm font-medium">{org.name}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(org.created_at).toLocaleDateString()}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Open tickets */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Open Tickets</CardTitle>
          </CardHeader>
          <CardContent>
            {(openTickets ?? []).length === 0 ? (
              <p className="text-justify text-sm text-muted-foreground">No open tickets.</p>
            ) : (
              <ul className="space-y-3">
                {(openTickets ?? []).map((ticket: any) => (
                  <li key={ticket.id} className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{ticket.subject}</div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(ticket.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <Badge className={cn('text-[10px] shrink-0', PRIORITY_COLORS[ticket.priority] ?? PRIORITY_COLORS.low)}>
                      {ticket.priority}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent platform activity, all orgs, no filter */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="w-4 h-4 text-muted-foreground" />
            Recent Platform Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(platformActivity ?? []).length === 0 ? (
            <p className="text-justify text-sm text-muted-foreground">No activity recorded yet.</p>
          ) : (
            <ul className="space-y-3">
              {(platformActivity ?? []).map((item: any) => (
                <li key={item.id} className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm">{item.description}</div>
                    <div className="text-xs text-muted-foreground">
                      {item.actor_name} · {item.organizations?.name ?? 'Unknown org'}
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {new Date(item.created_at).toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Onboarding progress */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Institution Onboarding Progress</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {(onboardingByOrg ?? []).length === 0 ? (
            <p className="text-justify text-sm text-muted-foreground px-4 pb-4">No institutions yet.</p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {(onboardingByOrg ?? []).map((org: any) => {
                  // org_onboarding is a 1:1 relation, embedded as a single object, not an array
                  const onboarding = org.org_onboarding ?? {}
                  const completed = ONBOARDING_STEPS.filter(s => onboarding[s.key]).length
                  const missing = ONBOARDING_STEPS.filter(s => !onboarding[s.key])
                  return (
                    <tr key={org.id} className="border-b last:border-0">
                      <td className="px-4 py-3 font-medium w-48">{org.name}</td>
                      <td className="px-2 py-3 w-40">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full bg-[#006B3C]"
                              style={{ width: `${Math.round((completed / ONBOARDING_STEPS.length) * 100)}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {completed} / {ONBOARDING_STEPS.length}
                          </span>
                        </div>
                      </td>
                      <td className="px-2 py-3 text-xs text-muted-foreground">
                        {missing.length === 0 ? (
                          <span className="text-green-600 font-medium">Complete</span>
                        ) : (
                          missing.map(s => s.label).join(', ')
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
